/**
 * Talks to our own server. This replaces the Base44 SDK client the app used to
 * depend on — same shape of calls, but against /api on the host that served
 * this page, authenticated with an httpOnly session cookie.
 */

export class ApiError extends Error {
  constructor(status, message, code, options) {
    super(message, options);
    this.status = status;
    this.code = code;
  }
}

async function request(method, path, body, { signal } = {}) {
  let res;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    throw new ApiError(0, "Can't reach the App Hub server", "offline", { cause });
  }

  if (res.status === 204) return null;

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, payload.error || `Request failed (${res.status})`, payload.code);
  return payload;
}

const get = (path, opts) => request("GET", path, undefined, opts);
const post = (path, body) => request("POST", path, body ?? {});
const patch = (path, body) => request("PATCH", path, body);
const del = (path) => request("DELETE", path, undefined);

export const api = {
  auth: {
    state: () => get("/api/auth/state"),
    setup: (body) => post("/api/auth/setup", body),
    login: (body) => post("/api/auth/login", body),
    logout: () => post("/api/auth/logout"),
    readInvite: (token) => get(`/api/auth/invite/${encodeURIComponent(token)}`),
    acceptInvite: (token, body) => post(`/api/auth/invite/${encodeURIComponent(token)}`, body),
    changePassword: (body) => post("/api/auth/password", body),
  },

  apps: {
    list: () => get("/api/apps"),
    listAll: () => get("/api/apps/all"),
    create: (body) => post("/api/apps", body),
    update: (id, body) => patch(`/api/apps/${id}`, body),
    remove: (id) => del(`/api/apps/${id}`),
    /** groups: [{ category_id, ids }] — one entry per category the drag touched. */
    reorder: (groups) => post("/api/apps/reorder", { groups }),

    async uploadIcon(file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/apps/icon", { method: "POST", credentials: "same-origin", body: form });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, payload.error || "Upload failed", payload.code);
      return payload.icon_url;
    },

    iconFromUrl: (url) => post("/api/apps/icon/from-url", { url }).then((r) => r.icon_url),
  },

  categories: {
    list: () => get("/api/categories"),
    create: (name) => post("/api/categories", { name }),
    rename: (id, name) => patch(`/api/categories/${id}`, { name }),
    remove: (id) => del(`/api/categories/${id}`),
    reorder: (ids) => post("/api/categories/reorder", { ids }),
  },

  admin: {
    network: () => get("/api/admin/network"),
    users: () => get("/api/admin/users"),
    inviteUser: (body) => post("/api/admin/users", body),
    updateUser: (id, body) => patch(`/api/admin/users/${id}`, body),
    removeUser: (id) => del(`/api/admin/users/${id}`),
    reinvite: (id) => post(`/api/admin/users/${id}/reinvite`),
    setPassword: (id, password) => post(`/api/admin/users/${id}/password`, { password }),
  },
};

/** Turns an invite token into the link an admin can send to someone. */
export const inviteUrl = (token, base = window.location.origin) =>
  `${base.replace(/\/$/, "")}/accept-invite?token=${encodeURIComponent(token)}`;

export const isLoopback = (origin) => /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|$)/i.test(origin);

/**
 * Every address the hub can be reached at, best first. A colleague cannot use a
 * localhost link, so it sorts last and is only ever a deliberate choice.
 */
export function reachableBases({ hostname, addresses = [], port } = {}) {
  const bases = [];
  if (hostname) bases.push(`http://${hostname}:${port}`);
  for (const address of addresses) bases.push(`http://${address}:${port}`);

  const current = window.location.origin;
  if (!bases.includes(current)) bases.push(current);

  return [...new Set(bases)].sort((a, b) => Number(isLoopback(a)) - Number(isLoopback(b)));
}
